<script setup lang="ts">
import { ElMessageBox } from "element-plus";
import { computed, inject } from "vue";
import {
  type WorkflowState,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import { customActionButtonInjectKey } from "~/NewFormBuilder/composables/useCustomActionButton";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { type ChangeStatusButtonFormElement } from "~/api";
import { showError } from "~/utils/handleAPIError";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props =
  defineProps<DefaultFormComponentProps<ChangeStatusButtonFormElement>>();

const { isReadOnly, inFlightRequestMap, getUnansweredQuestions } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const customActionHandler = inject(customActionButtonInjectKey, {
  handleCustomAction: (code: string) => {
    console.warn(
      "[ChangeStatusButton] No custom action handler registered for code:",
      code,
    );
  },
});

const { updateWorkflowStatus, getWorkflowStatus, deleteWorkflow } = inject(
  workflowInjectKey,
  {
    updateWorkflowStatus: async (status: string) => {
      console.log("No workflow in designer. Status to update to:", status);
    },
    updateWorkflowStatusFromNameStamp: async (status: string) => {
      console.log("No workflow in designer. Status to update to:", status);
    },
    getWorkflowStatus: () => {
      return "";
    },
    getWorkflow: () => {
      return undefined;
    },
    deleteWorkflow: undefined,
  },
) as WorkflowState;
// const answer = useAnswer<FormElementType.CHANGE_STATUS_BUTTON>(
//   props.modelValue,
// );
const isRequestInFlight = computed<boolean>(
  () => inFlightRequestMap.value.size > 0,
);
const elementProperties = computed(() => props.modelValue.properties);

const questionsRemaining = computed(() => {
  return getUnansweredQuestions();
});

const isDeleteAction = computed(
  () => elementProperties.value.deleteWorkflowOnClick === true,
);

const handleButtonClicked = async () => {
  if (isDeleteAction.value) {
    await handleDeleteWorkflow();
    return;
  }

  if (
    // !elementProperties.value.changeToStatus &&
    !elementProperties.value.changeWorkflowStatusTo
  ) {
    showError("No statuses provided to change to");
    return;
  }

  // if (elementProperties.value.changeToStatus) {
  //   await updateFormResponseStatus(elementProperties.value.changeToStatus);
  // }

  if (elementProperties.value.changeWorkflowStatusTo) {
    await updateWorkflowStatus(elementProperties.value.changeWorkflowStatusTo);
  }

  if (elementProperties.value.customActionCode && customActionHandler) {
    customActionHandler.handleCustomAction(
      elementProperties.value.customActionCode,
    );
  }
};

const handleDeleteWorkflow = async () => {
  if (!deleteWorkflow) {
    showError("Delete workflow is not available in this context");
    return;
  }

  try {
    await ElMessageBox.confirm(
      "Are you sure you want to permanently delete this workflow? This action cannot be undone.",
      "Delete Workflow",
      {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning",
        confirmButtonClass: "el-button--danger",
      },
    );

    await deleteWorkflow();
  } catch {
    // User cancelled the confirmation dialog
  }
};

const buttonDisabled = computed(() => {
  if (isDeleteAction.value) {
    return false;
  }
  return (
    // formResponse?.status === elementProperties.value.changeToStatus ||
    getWorkflowStatus() === elementProperties.value.changeWorkflowStatusTo ||
    (elementProperties.value.disableIfUnansweredQuestions
      ? questionsRemaining.value.length > 0
      : false)
  );
});

const showButton = computed(() => {
  return true;
  // don't wanna hide the button if the form is in edit mode
  // if (props.displayMode === FormDisplayMode.EDIT) {
  //   return true;
  // }
  // return (
  //   (elementProperties.value.showWhenStatus
  //     ? formResponse?.status === elementProperties.value.showWhenStatus
  //     : true) &&
  //   (elementProperties.value.hideWhenStatus
  //     ? !(elementProperties.value.hideWhenStatus === formResponse?.status)
  //     : true)
  // );
});
</script>

<template id="form-change-status-button-component">
  <div v-if="showButton" class="ChangeStatusButtonComponent">
    <slot name="label" :element="modelValue" />
    <el-button
      :type="isDeleteAction ? 'danger' : 'primary'"
      :disabled="buttonDisabled || isReadOnly(modelValue) || isRequestInFlight"
      @click="handleButtonClicked"
    >
      {{ elementProperties.buttonText ?? "Submit" }}</el-button
    >
  </div>
</template>

<style lang="less">
.ChangeStatusButtonComponent {
  display: flex;
  justify-content: space-between;
}
</style>
